/**
 * Per-security intraday price state (APP-3106).
 *
 * A security is "today" when its latest socket `priceAsOf` falls on the viewer's
 * local calendar date; otherwise (absent, invalid, or prior-dated) it is "prior".
 * Anchoring on the browser timezone keeps the verdict consistent with the local
 * time shown in the cell tooltip; viewers in different timezones may read the
 * same book differently.
 */

export const INTRADAY_PRICE_STATES = {
  TODAY: "today",
  PRIOR: "prior",
} as const

export type IntradayPriceState = (typeof INTRADAY_PRICE_STATES)[keyof typeof INTRADAY_PRICE_STATES]

function padDatePart(value: number): string {
  return String(value).padStart(2, "0")
}

export function getLocalCalendarDate(instant: string | number | null | undefined): string | null {
  if (instant == null || instant === "") {
    return null
  }
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`
}

/** The current viewer-local calendar date as "YYYY-MM-DD". */
export function getCurrentLocalDate(): string | null {
  return getLocalCalendarDate(Date.now())
}

export function getIntradayPriceState(priceAsOf: string | null | undefined, nowMs: number = Date.now()): IntradayPriceState {
  const priceDate = getLocalCalendarDate(priceAsOf)
  if (priceDate == null) {
    return INTRADAY_PRICE_STATES.PRIOR
  }
  return priceDate === getLocalCalendarDate(nowMs)
    ? INTRADAY_PRICE_STATES.TODAY
    : INTRADAY_PRICE_STATES.PRIOR
}

interface ContributorLeafValues {
  modelProviderId?: string
  economicExposure?: number
  priceAsOf?: string
}

export interface ContributorTreeNode {
  isFactor?: boolean
  notHeld?: boolean
  isNoLongerHeld?: boolean
  values?: Record<string, ContributorLeafValues | undefined>
  children?: Record<string, ContributorTreeNode | undefined>
}

export interface IntradayPricingSummary {
  /** Σ|weight| of today-priced holdings ÷ Σ|weight| of all holdings, in [0, 1]. */
  pricedTodayWeightRatio: number
  /** Today-priced holdings ÷ all holdings, by name count, in [0, 1]. */
  pricedTodayCountRatio: number
  /** Number of weighted holdings the ratios are computed over. */
  holdingCount: number
}

function isSecurityLeaf(node: ContributorTreeNode): boolean {
  return Object.values(node.values ?? {}).some(value => value?.modelProviderId)
}

function visitSecurityLeaves(node: ContributorTreeNode, visit: (leaf: ContributorTreeNode) => void): void {
  for (const child of Object.values(node.children ?? {})) {
    if (!child || child.isFactor || child.notHeld || child.isNoLongerHeld) {
      continue
    }
    if (isSecurityLeaf(child)) {
      visit(child)
      continue
    }
    visitSecurityLeaves(child, visit)
  }
}

export interface ComputeIntradayPricingSummaryOptions {
  /** Contributor values key carrying socket prices, e.g. "intraday". */
  intradayPrefix: string
  /** Contributor values key carrying weights, e.g. "composition". */
  compositionPrefix: string
  /** Viewer-local "YYYY-MM-DD" treated as today; defaults to the current local date. */
  today?: string | null
}

/**
 * Walks the full contributor tree (grouped views nest leaves under group nodes)
 * and aggregates the "Priced today" figure by gross economic exposure, so long
 * and short weights don't cancel. Returns null when there is nothing to weigh.
 */
export function computeIntradayPricingSummary(
  tree: ContributorTreeNode | null | undefined,
  { intradayPrefix, compositionPrefix, today = getCurrentLocalDate() }: ComputeIntradayPricingSummaryOptions,
): IntradayPricingSummary | null {
  if (!tree) {
    return null
  }

  let totalWeight = 0
  let todayWeight = 0
  let holdingCount = 0
  let todayCount = 0

  visitSecurityLeaves(tree, (leaf) => {
    const weight = Math.abs(Number(leaf.values?.[compositionPrefix]?.economicExposure))
    if (!Number.isFinite(weight) || weight === 0) {
      return
    }

    totalWeight += weight
    holdingCount += 1
    const priceDate = getLocalCalendarDate(leaf.values?.[intradayPrefix]?.priceAsOf)
    if (priceDate != null && priceDate === today) {
      todayWeight += weight
      todayCount += 1
    }
  })

  if (holdingCount === 0 || totalWeight === 0) {
    return null
  }

  return {
    pricedTodayWeightRatio: todayWeight / totalWeight,
    pricedTodayCountRatio: todayCount / holdingCount,
    holdingCount,
  }
}
